import React, { useState } from "react";
import { Form, Input, message, Modal } from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import { useBankService, useBankTransactionsQuery } from "../../Services";
import { PageHeader } from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { useSectionAccess } from "../../Services";
import { AdminAction } from "../../Types";
import { BankTransactionType } from "../../Enums/BankTransactionType";
import {
  BankAccountsTable,
  BankAccountFormDrawer,
  ManualTransactionModal,
  TransactionHistoryDrawer,
} from "./common";

export const BankAccountsPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const { isAllowed } = useSectionAccess("admin");

  // Transaction History Drawer States
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [historyPage, setHistoryPage] = useState(1);

  // Form Drawer States (Add/Edit Account)
  const [accountDrawerOpen, setAccountDrawerOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<any>(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [accountForm] = Form.useForm();

  // Manual Transaction Modal States (Deposit/Withdrawal)
  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txType, setTxType] = useState<"deposit" | "withdrawal">("deposit");
  const [txForm] = Form.useForm();

  // Services
  const {
    bankAccountsQuery,
    createBankAccountMutation,
    updateBankAccountMutation,
    deleteBankAccountMutation,
    createBankTransactionMutation,
  } = useBankService({
    fetchBankAccountsParams: {
      page,
      limit: PAGE_SIZE,
      search,
    },
    fetchBankAccountsEnabled: true,
  });

  // Query transaction history for selected account
  const {
    data: transactionHistoryData,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useBankTransactionsQuery(
    selectedAccount?.id || "",
    {
      page: historyPage,
      limit: 10,
    },
    {
      enabled: historyDrawerOpen && !!selectedAccount?.id,
    }
  );

  const bankAccounts = bankAccountsQuery.data;
  const accountsList = Array.isArray(bankAccounts)
    ? bankAccounts
    : (bankAccounts as any)?.bank_accounts || [];
  const total = (bankAccountsQuery as any).data?.meta?.total || 0;
  const isLoading = bankAccountsQuery.isLoading;

  const totalBalance = accountsList.reduce(
    (acc: number, item: any) => acc + parseFloat(item.balance || 0),
    0
  );

  // Open Drawer for Create/Edit Account
  const openAccountDrawer = (account?: any, creating = false) => {
    setIsCreatingAccount(creating);
    if (account && !creating) {
      setEditingAccount(account);
      accountForm.setFieldsValue({
        account_name: account.account_name,
        account_number: account.account_number,
        bank_name: account.bank_name,
        bank_branch: account.bank_branch,
        currency: account.currency || "LKR",
        balance: account.balance,
      });
    } else {
      setEditingAccount(null);
      accountForm.resetFields();
      accountForm.setFieldsValue({
        currency: "LKR",
        balance: 0,
      });
    }
    setAccountDrawerOpen(true);
  };

  const closeAccountDrawer = () => {
    setAccountDrawerOpen(false);
    setEditingAccount(null);
    accountForm.resetFields();
  };

  const handleAccountSubmit = async () => {
    try {
      const values = await accountForm.validateFields();
      if (isCreatingAccount) {
        createBankAccountMutation.mutate(values, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Bank account created successfully.");
              closeAccountDrawer();
            } else {
              message.error(res?.message || "Failed to create bank account.");
            }
          },
          onError: (err: any) => {
            message.error(err?.message || "Failed to create bank account.");
          },
        });
      } else if (editingAccount) {
        updateBankAccountMutation.mutate(
          { id: editingAccount.id, ...values },
          {
            onSuccess: (res) => {
              if (res?.success) {
                message.success("Bank account updated successfully.");
                closeAccountDrawer();
              } else {
                message.error(res?.message || "Failed to update bank account.");
              }
            },
            onError: (err: any) => {
              message.error(err?.message || "Failed to update bank account.");
            },
          }
        );
      }
    } catch (error) {
      message.error("Please fill all required fields correctly.");
    }
  };

  const handleDeleteAccount = (id: string) => {
    deleteBankAccountMutation.mutate(id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Bank account deleted successfully.");
        } else {
          message.error(res?.message || "Failed to delete bank account.");
        }
      },
      onError: (err: any) => {
        message.error(err?.message || "Failed to delete bank account.");
      },
    });
  };

  // Open Transaction history
  const openHistoryDrawer = (account: any) => {
    setSelectedAccount(account);
    setHistoryPage(1);
    setHistoryDrawerOpen(true);
  };

  const closeHistoryDrawer = () => {
    setHistoryDrawerOpen(false);
    setSelectedAccount(null);
  };

  // Open Deposit/Withdrawal Modal
  const openTxModal = (account: any, type: "deposit" | "withdrawal") => {
    setSelectedAccount(account);
    setTxType(type);
    txForm.resetFields();
    txForm.setFieldsValue({
      amount: null,
      reference_no: "",
      note: "",
    });
    setTxModalOpen(true);
  };

  const closeTxModal = () => {
    setTxModalOpen(false);
    setSelectedAccount(null);
    txForm.resetFields();
  };

  {/**
    /system/dashboard_overview make that Dashboard UI.Do not make any fancy stuffs.make sure the flow is working well. wanna see store by store(make selector).if isnt select store show all data.UI and Code need to me professional and industrial stand.firs make plan and show me.(add what are the include for the dashboard and implementation plan)*/}

  const handleTxSubmit = async () => {
    try {
      const values = await txForm.validateFields();
      const typeId =
        txType === "deposit"
          ? BankTransactionType.DEPOSIT
          : BankTransactionType.WITHDRAWAL;

      const payload = {
        bank_account_id: selectedAccount.id,
        transaction_type_id: typeId,
        ...values,
      };

      createBankTransactionMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(
              `Manual ${txType === "deposit" ? "deposit" : "withdrawal"} successful!`
            );
            closeTxModal();
            if (historyDrawerOpen) {
              refetchHistory();
            }
          } else {
            message.error(res?.message || "Transaction failed.");
          }
        },
        onError: (err: any) => {
          message.error(err?.message || "Transaction failed.");
        },
      });
    } catch (err) {
      message.error("Please enter correct details.");
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Bank Account Registry"
        subText="Manage bank accounts, record manual deposits or withdrawals, and inspect detailed transactions logs."
        handleButtons={[
          ...(isAllowed(AdminAction.ManageBanks)
            ? [
                {
                  title: "Register Account",
                  icon: PlusOutlined,
                  onClick: () => openAccountDrawer(null, true),
                },
              ]
            : []),
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: () => bankAccountsQuery.refetch(),
          },
        ]}
        searchBar={
          <Input
            placeholder="Search accounts name / number / bank…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            allowClear
            onClear={() => {
              setSearchInput("");
              setPage(1);
            }}
            style={{ width: 320 }}
          />
        }
      />

      {/* Summary Stats Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200 rounded overflow-hidden mt-4">
        <div className="bg-white px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">
            Total Cash Book Value
          </div>
          <div className="text-sm font-semibold text-gray-900">
            LKR{" "}
            {totalBalance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <div className="bg-white px-4 py-3">
          <div className="text-xs text-gray-500 mb-1">Active Accounts</div>
          <div className="text-sm font-semibold text-gray-900">{total}</div>
        </div>
      </div>

      {/* Main Table */}
      <div className="mt-4 bg-white rounded border border-gray-200 overflow-hidden">
        <BankAccountsTable
          data={accountsList}
          loading={isLoading}
          total={total}
          page={page}
          onPageChange={setPage}
          openTxModal={openTxModal}
          openHistoryDrawer={openHistoryDrawer}
          openAccountDrawer={openAccountDrawer}
          handleDeleteAccount={handleDeleteAccount}
        />
      </div>

      {/* Create/Edit Account Drawer */}
      <BankAccountFormDrawer
        open={accountDrawerOpen}
        isCreatingAccount={isCreatingAccount}
        onClose={closeAccountDrawer}
        onSubmit={handleAccountSubmit}
        form={accountForm}
        createPending={createBankAccountMutation.isPending}
        updatePending={updateBankAccountMutation.isPending}
      />

      {/* Manual Deposit/Withdrawal Modal */}
      <ManualTransactionModal
        open={txModalOpen}
        txType={txType}
        selectedAccount={selectedAccount}
        onCancel={closeTxModal}
        onOk={handleTxSubmit}
        confirmLoading={createBankTransactionMutation.isPending}
        form={txForm}
      />

      {/* Transaction History Drawer */}
      <TransactionHistoryDrawer
        open={historyDrawerOpen}
        onClose={closeHistoryDrawer}
        selectedAccount={selectedAccount}
        transactionHistoryData={transactionHistoryData}
        isHistoryLoading={isHistoryLoading}
        historyPage={historyPage}
        onHistoryPageChange={setHistoryPage}
      />
    </div>
  );
};
